import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@emotion/react';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import EditOutlined from '@mui/icons-material/EditOutlined';
import TextField from '@material-ui/core/TextField';
import { withStyles } from '@mui/styles';
import EditBackstageDescModal from 'components/organisms/EditBackstageDescModal';
import { useAppSelector } from 'app/hooks';
import { selectAuth } from 'features/auth/authSlice';
import { UserData, UserProfileData } from 'interfaces/UserData.interface';
import Clamp from 'react-multiline-clamp';
import { Alert, Stack } from '@mui/material';
import Loader from 'components/atoms/loader';
import BackstagePrompt from 'views/DesktopViews/OtherProfilePage/ProfilePageBody/BackstagePrompt';
import { NormalButton } from 'components/atoms/Button';
import { checkSub, handleToggleSubscribe } from 'APIs/utils';
import { useParams } from 'react-router-dom';
import Backstage from 'interfaces/Backstage.interface';
import { getBackstageByUsername, getUsersBackstage } from 'APIs/BackstageApi';
import BackstageProfileCard from 'components/molecules/Card/BackstageProfileCard';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import { useDispatch } from 'react-redux';
const etherSvg = require('assets/ethereum.svg');

const colortheme = createTheme({
  palette: {
    primary: {
      main: '#292739',
    },
  },
  typography: {
    fontFamily: ['lato'].join(','),
  },
});

const WhiteTextField = withStyles({
  root: {
    '& .MuiInputBase-input': {
      color: '#fff', // Text color
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#fff8', // Semi-transparent underline
    },
    '& .MuiInput-underline:hover:before': {
      borderBottomColor: '#fff', // Solid underline on hover
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#fff', // Solid underline on focus
    },
  },
})(TextField);

const Body = styled(Grid)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  paddingTop: '35px',
  paddingBottom: '120px',
  paddingLeft: '44px',
  paddingRight: '44px',
  gap: '6px',
}));

const ContentGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  gap: '6px',
}));

const EditLayer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'end',
  justifyContent: 'end',
  gap: '3px',
  color: '#ddb930',
  position: 'absolute',
  right: -35,
  top: 0,
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 'bold',
  color: '#ffffff',
  textAlign: 'center',
  width: '100%',
}));

const EditText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#ddb930',
}));

const SubText = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  color: '#ffffff',
  fontWeight: 'bold',
}));

const SubBtnText1 = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#c1c1c1',
}));

const SubBtnText2 = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#36899f',
}));

const SubButton = styled(Box)(({ theme }) => ({
  backgroundColor: '#ddb930',
  width: '100%',
  height: '65px',
  borderRadius: '25px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const Bottom = styled(Box)(({ theme }) => ({
  paddingTop: '15px',
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
}));

const imgStyles = {
  width: '30px',
  height: '30px',
  margin: '0px 5px',
} as React.CSSProperties;

interface IPaywallBody {
  isFeed: any;
  data?: UserProfileData;
}

const PaywallBody = ({ isFeed, data }: IPaywallBody) => {
  const [subText, setSubText] = React.useState<string>(
    data?.backstageDesc || ''
  );
  const authState = useAppSelector(selectAuth);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [, setBackstageData] = React.useState<Backstage[]>([]);
  const { username } = useParams();
  const [subscribed, setSubscribed] = React.useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = React.useState<boolean>(false);
  const [subLoading, setSubLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<boolean>(false);
  const [transaction, setTransaction] = React.useState<boolean>(false);
  const [isSubTextFocused, setIsSubTextFocused] =
    React.useState<boolean>(false);
  const dispatch = useDispatch();
  const fetchData = async () => {
    if (username) {
      let userData: Backstage[] | undefined = [];
      if (authState.username === username) {
        userData = await getUsersBackstage();
      } else {
        userData = await getBackstageByUsername(username);
      }
      if (userData) {
        setBackstageData(userData);
      }
    }
  };

  React.useEffect(() => {
    !subscribed &&
      checkSub(
        setSubLoading,
        authState,
        setSubscribed,
        setIsSubscribed,
        setTransaction,
        dispatch,
        data as Partial<UserData>,
        false,
        fetchData
      );
    //eslint-disable-next-line
  }, [authState.id, data?.id]);

  return (
    <React.Fragment>
      <ThemeProvider theme={colortheme}>
        {data ? (
          <Body container>
            <ContentGrid sx={{ position: 'relative' }} item xs={12}>
              <TitleText>👋 Welcome to my Backstage! </TitleText>
              {authState.username === data.username && (
                <EditLayer>
                  <EditBackstageDescModal userId={data.id}>
                    <EditOutlined fontSize="small" />
                    <EditText>Edit</EditText>
                  </EditBackstageDescModal>
                </EditLayer>
              )}
            </ContentGrid>
            <ContentGrid item xs={12}>
              {!isSubTextFocused ? (
                data?.backstageDesc && (
                  <Typography
                    component={'span'}
                    sx={{
                      color: '#c1c1c1',
                      fillRule: 'evenodd',
                      textAlign: 'center',
                      width: '100%',
                      marginTop: '20px',
                    }}
                  >
                    <Clamp
                      showMoreElement={({ toggle }: any) => (
                        <span
                          role="button"
                          onClick={toggle}
                          style={{
                            color: '#ddb930',
                            cursor: 'pointer',
                            WebkitUserSelect: 'none',
                          }}
                        >
                          See more
                        </span>
                      )}
                      showLessElement={({ toggle }: any) => (
                        <span
                          role="button"
                          onClick={toggle}
                          style={{
                            color: '#ddb930',
                            cursor: 'pointer',
                            WebkitUserSelect: 'none',
                          }}
                        >
                          See Less
                        </span>
                      )}
                      withTooltip
                      lines={2}
                      withToggle
                    >
                      <span>{data.backstageDesc}</span>
                    </Clamp>
                  </Typography>
                )
              ) : (
                <WhiteTextField
                  style={{ width: '100%' }}
                  multiline
                  autoFocus
                  value={subText}
                  onChange={event => setSubText(event.target.value)}
                  onBlur={event => setIsSubTextFocused(false)}
                />
              )}
            </ContentGrid>
            {authState.id !== data?.id && !subscribed && (
              <>
                {/* <ContentGrid item xs={12} sx={{ paddingTop: '41px' }}>
                <SubText>Subscribe to support {username}</SubText>
              </ContentGrid> */}
                {subLoading ? (
                  <Loader size="2" sx={{ width: '100%', margin: '20px 0px' }} />
                ) : (
                  <ContentGrid
                    item
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'column',
                      gap: '20px',
                    }}
                    // onClick={props.Feed}
                  >
                    <BackstagePrompt
                      transaction={transaction}
                      loading={loading}
                    >
                      <NormalButton
                        sx={{
                          width: '50vw',
                          maxWidth: '400px',
                          textTransform: 'uppercase',
                        }}
                        text={
                          loading ? (
                            <Loader />
                          ) : transaction ? (
                            'Waiting on block chain transaction'
                          ) : (
                            'Subscribe'
                          )
                        }
                        color="yellow"
                        onClick={() =>
                          handleToggleSubscribe(
                            authState,
                            setError,
                            setTransaction,
                            setLoading,
                            setIsSubscribed,
                            dispatch,
                            false,
                            '0.00012',
                            data as Partial<UserData>
                          )
                        }
                        isDisabled={isSubscribed || transaction}
                      />
                    </BackstagePrompt>
                    <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                      $5.99/mo or{' '}
                      <img
                        style={imgStyles}
                        src={etherSvg.default}
                        alt="ether"
                      />{' '}
                      0.0012ETH/mo
                    </Typography>
                  </ContentGrid>
                )}
                <Stack
                  direction={'column'}
                  spacing={2}
                  sx={{ width: '100%', marginTop: '10px ' }}
                >
                  {transaction && (
                    <Alert
                      onClose={() => setTransaction(false)}
                      severity="success"
                    >
                      Thanks for supporting {data?.username}! We're waiting for
                      the blockchain to finish confirming the transaction before
                      we can unlock the content for you. Please check back in
                      about 15 minutes!
                    </Alert>
                  )}
                  {error && (
                    <Alert onClose={() => setError(false)} severity="error">
                      There was an error in following {data?.username}
                    </Alert>
                  )}
                </Stack>
              </>
            )}
            {username !== authState.username && (
              <Typography
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  marginTop: '60px',
                  marginBottom: '20px',
                }}
                variant="h5"
              >
                Member Benefits
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '20px',
                    justifyContent: 'center',
                    margin: '40px 0px',
                  }}
                >
                  <BackstageProfileCard
                    icon={<DescriptionOutlinedIcon />}
                    heading="posts you have access"
                    number={120}
                    extraText="160 Added"
                  />
                  <BackstageProfileCard
                    icon={<ImageOutlinedIcon />}
                    heading="photos you have access"
                    number={500}
                    extraText="659 Added"
                  />
                  <BackstageProfileCard
                    icon={<VideocamOutlinedIcon />}
                    heading="videos you have access"
                    number={75}
                    extraText="107 Added"
                  />
                  <BackstageProfileCard
                    icon={<FavoriteBorderOutlinedIcon />}
                    heading="likes on all posts"
                    number={10000}
                  />
                </Box>
              </Typography>
            )}
            <ContentGrid item xs={12} sx={{ paddingTop: '41px' }}>
              <SubText>Subscribe to support {data.username}</SubText>
            </ContentGrid>
            <ContentGrid
              item
              xs={12}
              sx={{ paddingTop: '18px' }}
              onClick={isFeed}
            >
              <SubButton sx={{ maxWidth: '1000px' }}>
                <Typography
                  component={'span'}
                  sx={{
                    color: '#ffffff',
                    fillRule: 'evenodd',
                    fontSize: '16px',
                    fontWeight: 'bold',
                  }}
                >
                  <span>0.0012 ETH</span>
                  <span
                    style={{
                      color: '#ffffff',
                      position: 'relative',
                      top: '-3px',
                    }}
                  >
                    /month
                  </span>
                </Typography>
              </SubButton>
            </ContentGrid>
            <Bottom>
              <SubBtnText1>Current balance: </SubBtnText1>
              <SubBtnText2>&nbsp; 0.1299 &nbsp; ETH</SubBtnText2>
            </Bottom>
            <Bottom>
              <SubBtnText1>After subscribing: </SubBtnText1>
              <SubBtnText2>&nbsp; 0.1287 &nbsp; ETH</SubBtnText2>
            </Bottom>
          </Body>
        ) : (
          <></>
        )}
      </ThemeProvider>
    </React.Fragment>
  );
};

export default PaywallBody;
